import { ContentGroup, Grid, GridItem, Notification, Paragraph, } from '@imported/constellation';
import React, { useCallback, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';
import { useIdvProvider } from '../../context/index.js';
import { useLabelContext } from '../../context/LabelContext.js';
import { generateLinkValue, JOURNEY_EVENT, TAGGING_FIELDS, } from '../../tagging/index.js';
import { BIO_METRIC_STATUS, COUNTRY, PROOF_IDENTITY_SELECTED_OPTION, } from '../FormFields/fieldNames.js';
import { DropdownWrapper, SelectorWrapper, VerticalSpace, } from '../FormFields/index.js';
import RadioButtonGroupWrapper from '../FormFields/RadioButtonGroupWrapper/index.js';
import { CommonFormStyleWrapper } from '../FormFields/styles.js';
import Navigation from '../Navigation/index.js';
import { Link } from '../Tagging/index.js';
var CheckboxState;
(function (CheckboxState) {
    CheckboxState["NO"] = "NO";
    CheckboxState["YES"] = "YES";
})(CheckboxState || (CheckboxState = {}));
const requiredError = (getLabelFromContext) => ({
    required: getLabelFromContext('components.proofOfIdentity.formValidation.requiredOptionField'),
});
const countryList = (getLabelFromContext) => {
    const countries = getLabelFromContext('components.countryList');
    return countries.map((country) => {
        if (typeof country === 'string') {
            return {
                label: country,
                value: country,
                disabled: false,
            };
        }
        else {
            return country;
        }
    });
};
const ProofOfIndentityForm = ({ methods, handleProofOfIdentitySubmission, handlePreviousPage, }) => {
    const { control, formState: { errors }, setValue, } = methods;
    const { idvState } = useIdvProvider();
    const { identityConfirmationByPost } = idvState;
    const { getLabelFromContext } = useLabelContext();
    const [options, setOptions] = useState(getLabelFromContext('components.proofOfIdentity.options'));
    const [selectOption, setSelectOption] = useState('');
    const [notification, setNotification] = useState('');
    const [checkedValue, setCheckedValue] = useState(undefined);
    const theme = useTheme();
    const isTabletOrMobile = useMediaQuery({
        query: `(max-width: ${theme.breakpoint_md})`,
    });
    const handleBusinessPrivacyClick = useCallback(() => {
        window.location.href = getLabelFromContext('components.proofOfIdentity.navigation.businessPrivacyLink');
    }, [getLabelFromContext]);
    const selectedValue = useCallback((event) => {
        const currentValue = event?.target?.value;
        setOptions((prevState) => prevState.map((item) => {
            if (item.value === currentValue) {
                setSelectOption(currentValue);
                return {
                    ...item,
                    checked: true,
                };
            }
            else {
                return {
                    ...item,
                    checked: false,
                };
            }
        }));
        setNotification('');
        setValue(COUNTRY, '');
        setValue(BIO_METRIC_STATUS, '');
        setCheckedValue(undefined);
    }, [setValue]);
    const handleCountryChange = useCallback((event) => {
        setNotification(event.currentTarget.value);
    }, []);
    const handleBioMetricSelector = useCallback((event) => {
        if (event.target.value === 'No') {
            setCheckedValue(CheckboxState.NO);
            setValue(BIO_METRIC_STATUS, 'No');
        }
        else {
            setCheckedValue(CheckboxState.YES);
            setValue(BIO_METRIC_STATUS, 'Yes');
        }
    }, [setValue]);
    const [passportOption, drivingLicenseOption, otherOption] = options;
    const isPassportOptionSelected = selectOption === passportOption.value;
    const isDrivingLicenseOptionSelected = selectOption === drivingLicenseOption.value;
    const isOtherOption = selectOption === otherOption.value;
    const isNotBioMetric = checkedValue === CheckboxState.NO;
    return (React.createElement(CommonFormStyleWrapper, null,
        React.createElement(Grid, null,
            React.createElement(GridItem, null,
                React.createElement(ContentGroup, null,
                    React.createElement(Paragraph, null, getLabelFromContext('components.proofOfIdentity.description.one')),
                    React.createElement(Paragraph, null, getLabelFromContext('components.proofOfIdentity.description.two')),
                    React.createElement(Paragraph, null,
                        getLabelFromContext('components.proofOfIdentity.description.three'),
                        React.createElement(Link, { eventAction: JOURNEY_EVENT.BUTTON_CLICK, journeyEvent: JOURNEY_EVENT.INTERNAL_CLICK, linkValue: generateLinkValue(TAGGING_FIELDS.PRIVACY_POLICY_LINK), taggingLabel: TAGGING_FIELDS.PRIVACY_POLICY_LINK, onClick: handleBusinessPrivacyClick }, getLabelFromContext('components.proofOfIdentity.description.link'))),
                    React.createElement(RadioButtonGroupWrapper, { control: control, customOnChange: selectedValue, extendMargin: 24, label: 'Select the type of photo ID you want to use', name: PROOF_IDENTITY_SELECTED_OPTION, options: options, radioButtonGroupProps: {
                            label: '',
                            error: errors[PROOF_IDENTITY_SELECTED_OPTION]?.message,
                            name: PROOF_IDENTITY_SELECTED_OPTION,
                            taggingLabel: TAGGING_FIELDS.PROOF_IDENTITY_SELECTED_OPTION,
                        }, validation: requiredError(getLabelFromContext) }),
                    (isPassportOptionSelected || isDrivingLicenseOptionSelected) && (React.createElement(Notification, null, isPassportOptionSelected
                        ? getLabelFromContext('components.proofOfIdentity.notification.passport')
                        : getLabelFromContext('components.proofOfIdentity.notification.drivingLicense'))),
                    isPassportOptionSelected && (React.createElement(React.Fragment, null,
                        React.createElement(VerticalSpace, { space: 10 }),
                        React.createElement(DropdownWrapper, { control: control, customOnChange: handleCountryChange, disabledIndex: 2, dropdownOptions: countryList(getLabelFromContext), dropdownProps: {
                                label: getLabelFromContext('components.proofOfIdentity.dropDownLabel'),
                                name: COUNTRY,
                                error: errors[COUNTRY]?.message,
                                taggingLabel: TAGGING_FIELDS.PASSPORT_COUNTRY_OF_ISSUE,
                                inputWidth: isTabletOrMobile ? 'fluid' : 'default',
                                dropdownTaggingValue: true,
                                isQA: true,
                            }, extendMargin: 24, name: COUNTRY, validation: {
                                required: getLabelFromContext('components.proofOfIdentity.formValidation.requiredField'),
                            } }))),
                    (notification || isDrivingLicenseOptionSelected) && (React.createElement(React.Fragment, null,
                        React.createElement(VerticalSpace, { space: 10 }),
                        React.createElement(SelectorWrapper, { control: control, customOnChange: handleBioMetricSelector, extendMargin: 24, name: BIO_METRIC_STATUS, noChecked: checkedValue === CheckboxState.NO, selectorOptions: getLabelFromContext('components.proofOfIdentity.toggleButtonSelectorOptions'), selectorProps: {
                                label: identityConfirmationByPost
                                    ? getLabelFromContext('components.proofOfIdentity.toggleButtonPost')
                                    : getLabelFromContext('components.proofOfIdentity.toggleButton'),
                                supportiveText: identityConfirmationByPost
                                    ? getLabelFromContext('components.proofOfIdentity.toggleButtonSupportiveTextPost')
                                    : getLabelFromContext('components.proofOfIdentity.toggleButtonSupportiveText'),
                                error: errors[BIO_METRIC_STATUS]?.message,
                                name: BIO_METRIC_STATUS,
                                taggingLabel: TAGGING_FIELDS.BIO_METRIC_STATUS,
                            }, validation: {
                                required: getLabelFromContext('components.proofOfIdentity.formValidation.requiredOptionField'),
                            }, yesChecked: checkedValue === CheckboxState.YES }))),
                    (((isPassportOptionSelected || isDrivingLicenseOptionSelected) &&
                        isNotBioMetric) ||
                        isOtherOption) && (React.createElement(React.Fragment, null,
                        React.createElement(Notification, { heading: identityConfirmationByPost
                                ? getLabelFromContext('components.proofOfIdentity.notification.warningForPost.heading')
                                : getLabelFromContext('components.proofOfIdentity.notification.warning.heading'), headingLevel: 2, sentiment: 'warning' },
                            identityConfirmationByPost
                                ? getLabelFromContext('components.proofOfIdentity.notification.warningForPost.desc1')
                                : getLabelFromContext('components.proofOfIdentity.notification.warning.desc1'),
                            !identityConfirmationByPost && (React.createElement(React.Fragment, null,
                                React.createElement(VerticalSpace, { space: 10 }),
                                getLabelFromContext('components.proofOfIdentity.notification.warning.desc2'))),
                            React.createElement(VerticalSpace, { space: 10 })),
                        React.createElement(VerticalSpace, { space: 10 }))),
                    React.createElement(Navigation, { previousLabel: getLabelFromContext('components.proofOfIdentity.navigation.previousLabel'), submitLabel: getLabelFromContext('components.proofOfIdentity.navigation.submitLabel'), handleNext: handleProofOfIdentitySubmission, handlePrevious: handlePreviousPage }))))));
};
export default ProofOfIndentityForm;
//# sourceMappingURL=index.js.map